import { useMemo } from "react";
import clsx from "clsx";
import { ListBulletIcon } from "@heroicons/react/24/outline";

/**
 * One row of the matrix: a list, and where each of its two checkboxes writes.
 *
 * Routing lives on the row rather than the section so that ticker, keyword and
 * tag watchlists can share a single table while each still writes to its own
 * underlying selection key. Which of the three a list happens to be is storage
 * detail — the user shouldn't have to know, or navigate by it.
 */
export interface MatrixRow {
  id: string;
  label: string;
  /** Grouping label, e.g. "Yours" / "Newsquawk". */
  group: string;
  /** Selection key the Highlight checkbox writes to; omit if unavailable. */
  highlightSectionId?: string;
  /** Selection key the Filter checkbox writes to; omit if unavailable. */
  filterSectionId?: string;
  /**
   * Storage-family marker (T / K / #). No longer rendered — this view stopped
   * organising by our storage split, so it was labelling a distinction the
   * reader cannot act on. Kept because the matrix still groups by family.
   */
  kind?: string;
  /**
   * Several lists joined with AND/OR, rather than one flat set of terms.
   *
   * The only storage distinction the reader is shown, because it is the only
   * one that changes what they can do: two of these cannot filter at once. The
   * ticker/keyword/tag split stays hidden — it changes nothing for them.
   */
  combined?: boolean;
  highlightDisabled?: boolean;
  filterDisabled?: boolean;
  /**
   * Why the Filter checkbox is off, on the row itself.
   *
   * The tip above the list can be dismissed for good, so the explanation has
   * to survive somewhere weightless. A title answers "why is that grey?" at
   * the moment it is asked and costs nothing when it is not.
   */
  filterDisabledReason?: string;
}

interface MatrixSectionProps {
  rows: MatrixRow[];
  selections: Record<string, string[]>;
  onToggle: (sectionId: string, optionId: string, isDisabled: boolean) => void;
  /**
   * Which application this panel is for. Highlighting and filtering are
   * functionally different and each has its own header indicator, so a panel
   * shows one of them — not both columns with the other greyed.
   */
  column: "highlight" | "filter";
  /** Opens the contents of a row. Omit to leave names inert. */
  /**
   * `sectionId` is required to identify the row, not decoration: row ids are
   * bare record ids from three separate stores (portfolios, tag watchlists,
   * headline queries), each with its own sequence, so "7" alone is ambiguous.
   */
  onInspect?: (rowId: string, sectionId: string) => void;
  conflictNote?: React.ReactNode;
  searchQuery?: string;
  emptyLabel?: string;
}

function Checkbox({
  checked,
  disabled,
  label,
  title,
  onChange,
}: {
  checked: boolean;
  disabled: boolean;
  label: string;
  /** Why it's disabled, when it is. */
  title?: string;
  onChange: () => void;
}) {
  return (
    <label
      title={title}
      className={clsx(
        "flex items-center justify-center py-1",
        disabled ? "cursor-not-allowed" : "cursor-pointer"
      )}
    >
      <span className="sr-only">{label}</span>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        className={clsx(
          "size-3.5 rounded border-gray-600 bg-gray-900 text-squawk-blue",
          "focus:ring-1 focus:ring-squawk-blue focus:ring-offset-0",
          disabled ? "cursor-not-allowed" : "cursor-pointer"
        )}
      />
    </label>
  );
}

/**
 * Every list a widget can apply, in one list — whether it holds tickers,
 * keywords or tags, and whoever owns it.
 *
 * Replaces a panel that split the same lists across sections by storage type.
 * Which application (highlight or filter) this instance is for comes from
 * `column`: the two are functionally different, each has its own header
 * indicator, so each gets its own panel rather than sharing a two-column grid.
 */
export default function MatrixSection({
  rows,
  selections,
  onToggle,
  column,
  onInspect,
  conflictNote,
  searchQuery,
  emptyLabel = "No options available",
}: MatrixSectionProps) {
  const sectionOf = (row: MatrixRow) =>
    column === "highlight" ? row.highlightSectionId : row.filterSectionId;

  const groups = useMemo(() => {
    const query = searchQuery?.trim().toLowerCase();
    // A list that can't be applied this way isn't shown at all — with no
    // second column to explain it, a permanently disabled row is just noise.
    const applicable = rows.filter((row) =>
      column === "highlight" ? row.highlightSectionId : row.filterSectionId
    );
    const visible = query
      ? applicable.filter((row) => row.label.toLowerCase().includes(query))
      : applicable;

    const byGroup = new Map<string, MatrixRow[]>();
    for (const row of visible) {
      const existing = byGroup.get(row.group);
      if (existing) existing.push(row);
      else byGroup.set(row.group, [row]);
    }
    return [...byGroup.entries()].map(([label, groupRows]) => ({
      label,
      rows: groupRows,
    }));
  }, [rows, searchQuery, column]);

  if (groups.length === 0) {
    return <p className="px-1 py-2 text-xs text-gray-500">{emptyLabel}</p>;
  }

  return (
    <div className="space-y-1.5">
      {/* Filter-only. Nothing sets highlightDisabled — you can highlight as
          many watchlists as you like, combined or not — so in the Highlight
          panel this explains a limit that doesn't exist there. */}
      {column === "filter" && conflictNote}

      {groups.map((group) => (
        <div key={group.label}>
          {group.label && (
            <p className="px-1 pb-0.5 pt-1 text-2xs font-semibold uppercase tracking-wider text-gray-500">
              {group.label}
            </p>
          )}
          {group.rows.map((row) => {
            const sectionId = sectionOf(row)!;
            const checked = (selections[sectionId] || []).includes(row.id);
            const disabled =
              column === "highlight" ? !!row.highlightDisabled : !!row.filterDisabled;

            return (
              <div
                // Section-qualified: portfolio 7 and tag watchlist 7 are both
                // "Yours" and both id "7", so grouping alone gave two siblings
                // the same key — React then rendered the pair unreliably and
                // the row could not be opened.
                key={`${sectionId}-${row.id}`}
                /**
                 * Disabled state belongs to the row, not its parts.
                 *
                 * It used to be applied per element — opacity on the checkbox,
                 * a dimmer grey on the name, nothing at all on the Combined
                 * marker — so the marker ended up brighter than the name it
                 * sat beside. Fading the container makes every child agree by
                 * construction, including whatever gets added next.
                 */
                className={clsx(
                  "flex items-center gap-2 rounded px-1",
                  disabled ? "opacity-40" : "hover:bg-gray-700/30"
                )}
              >
                <div className="w-6 shrink-0">
                  <Checkbox
                    checked={checked}
                    disabled={disabled}
                    label={row.label}
                    title={disabled ? row.filterDisabledReason : undefined}
                    onChange={() => onToggle(sectionId, row.id, disabled)}
                  />
                </div>
                {/* The name selects.
                    It used to open the contents instead, on the reasoning that
                    "which list is this?" and "use this list" are different
                    questions. They are — but only one of them is what someone
                    clicking a row in a filter menu is asking. Everywhere else
                    in the product a labelled checkbox row toggles when you hit
                    the label, and here it silently opened a modal instead, so
                    the gesture that looked like selecting did anything but.
                    Contents moved to the button beside it. */}
                <button
                  type="button"
                  onClick={() => onToggle(sectionId, row.id, disabled)}
                  disabled={disabled}
                  className={clsx(
                    "min-w-0 flex-1 truncate py-1 text-left text-xs transition-colors",
                    // One grey. The row's own opacity carries the disabled
                    // state now; dimming the text as well double-faded it.
                    "text-gray-200",
                    !disabled && "cursor-pointer hover:text-white"
                  )}
                  title={disabled ? row.filterDisabledReason : row.label}
                >
                  {row.label}
                </button>
                {onInspect && (
                  <button
                    type="button"
                    onClick={(e) => {
                      // The row is a selection target now; inspecting must not
                      // read as one.
                      e.stopPropagation();
                      onInspect(row.id, sectionId);
                    }}
                    className="shrink-0 rounded p-1 text-gray-500 transition-colors hover:bg-gray-700/60 hover:text-squawk-blue"
                    title={`${row.label} — see what's in it`}
                    aria-label={`See what's in ${row.label}`}
                  >
                    <ListBulletIcon className="size-3.5" />
                  </button>
                )}
                {/* A modifier, not a badge. Bordered and uppercase it fought
                    the watchlist name on every row it appeared on, for
                    something that qualifies the name rather than labels it. */}
                {row.combined && (
                  <span
                    title="Several watchlists joined with AND/OR rules"
                    // Filled rather than bordered, sentence case rather than
                    // caps: the bordered badge fought the name, and plain
                    // gray-500 text went far enough the other way that it
                    // stopped registering. A soft chip reads as attached to
                    // the row without competing with it.
                    className="shrink-0 rounded bg-gray-700/70 px-1.5 py-px text-2xs text-gray-300"
                  >
                    Combined
                  </span>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
